import React from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
	text?: string;
	lcolor?: string;
	wrapperClass?: string;
	style?: React.CSSProperties;
	/**
	 * When set, the spinner is centered (horizontally + vertically) in a band rather than
	 * rendered as a bare inline spinner. Used for every "content is loading" phase so the
	 * loader behaves identically everywhere — lazy chunk download, community-readiness gate,
	 * and each route's data fetch:
	 *  - 'full'    → fills the content area; for an empty initial load / chunk / gate.
	 *  - 'compact' → a short band; for "load more" while content already sits above.
	 * Omitted → the original bare inline spinner (unchanged for existing callers).
	 */
	region?: 'full' | 'compact';
}

export default function ContainerLoader({ text, lcolor, wrapperClass, style, region }: IProps) {
	const { t } = useTranslation('next-app');

	const spinner = (
		<div
			className={`loaderContainer spinner-border text-${lcolor || 'success'} ${wrapperClass ?? ''}`}
			role="status"
			style={style}
		>
			<span className="sr-only">
				{t('containerLoader.loading', 'Loading')} {text}...
			</span>
		</div>
	);

	if (!region) return spinner;

	// flex:1/width:100% so it fills the content area (e.g. as a flex child of the row-flex
	// .main-container) instead of collapsing to the spinner's width and hugging the sidebar.
	// minHeight gives the spinner a band to center within.
	return (
		<div
			className="text-center"
			style={{
				flex: 1,
				width: '100%',
				minWidth: 0,
				minHeight: region === 'compact' ? '120px' : '80vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			{spinner}
		</div>
	);
}
